import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

dayjs.extend(customParseFormat)

type DateAccuracy = 'day' | 'month' | 'year'

export interface FuzzyDateRange {
  from: Date
  to: Date
  accuracy: DateAccuracy
  value: Date
}

export const parseDate = (date?: string | null, format = 'YYYY-MM-DD') => {
  if (date === undefined || date === null) {
    return undefined
  }
  const parsed = dayjs(date, format)
  if (!parsed.isValid()) {
    return undefined
  }
  return parsed.toDate()
}

export const fuzzyParseDate = (date?: string) => {
  if (!date) {
    return undefined
  }
  if (date === 'today' || date === 'now' || date === 'heute') {
    return {
      from: dayjs().startOf('day').toDate(),
      to: dayjs().endOf('day').toDate(),
      accuracy: 'day' as DateAccuracy,
      value: dayjs().toDate(),
    }
  }
  if (date === 'yesterday' || date === 'gestern') {
    return {
      from: dayjs().subtract(1, 'day').startOf('day').toDate(),
      to: dayjs().subtract(1, 'day').endOf('day').toDate(),
      accuracy: 'day' as DateAccuracy,
      value: dayjs().subtract(1, 'day').toDate(),
    }
  }
  const parts = date.split('.')
  if (parts.length === 3) {
    return {
      from: dayjs(date, 'DD.MM.YYYY').startOf('day').toDate(),
      to: dayjs(date, 'DD.MM.YYYY').endOf('day').toDate(),
      accuracy: 'day' as DateAccuracy,
      value: dayjs(date, 'DD.MM.YYYY').toDate(),
    }
  }
  if (parts.length === 2) {
    return {
      from: dayjs(date, 'MM.YYYY').startOf('month').toDate(),
      to: dayjs(date, 'MM.YYYY').endOf('month').toDate(),
      accuracy: 'month' as DateAccuracy,
      value: dayjs(date, 'MM.YYYY').toDate(),
    }
  }
  if (parts.length === 1) {
    if (parts[0]?.length === 2) {
      return {
        from: dayjs(date, 'YY').startOf('year').toDate(),
        to: dayjs(date, 'YY').endOf('year').toDate(),
        accuracy: 'year' as DateAccuracy,
        value: dayjs(date, 'YY').toDate(),
      }
    }
    if (parts[0]?.length === 4) {
      return {
        from: dayjs(date, 'YYYY').startOf('year').toDate(),
        to: dayjs(date, 'YYYY').endOf('year').toDate(),
        accuracy: 'year' as DateAccuracy,
        value: dayjs(date, 'YYYY').toDate(),
      }
    }
  }
  return undefined
}

export function formatDate(date: string | Date | null | undefined, defaultValue = ''): string {
  if (date === null || date === undefined || date === '') {
    return defaultValue
  }
  const value = dayjs(date)
  if (value.isValid()) {
    return value.tz('Europe/Berlin').format('DD.MM.YYYY')
  }
  return defaultValue
}

export function formatDateTime(date: string | Date | null | undefined, defaultValue = ''): string {
  if (date === null || date === undefined || date === '') {
    return defaultValue
  }
  const value = dayjs(date)
  if (value.isValid()) {
    return value.tz('Europe/Berlin').format('DD.MM.YYYY HH:mm:ss')
  }
  return defaultValue
}

export function formatTime(date: string | Date | null | undefined, defaultValue = ''): string {
  if (date === null || date === undefined || date === '') {
    return defaultValue
  }
  const value = dayjs(date)
  if (value.isValid()) {
    return value.tz('Europe/Berlin').format('HH:mm')
  }
  return defaultValue
}

export function formateByType(
  date: string | Date | null | undefined,
  dateType: 'date' | 'date-time' | 'time' = 'date-time',
  defaultValue = '',
): string {
  if (date === null || date === undefined || date === '') {
    return defaultValue
  }
  const value = dayjs(date)
  if (value.isValid()) {
    switch (dateType) {
      case 'date':
        return value.tz('Europe/Berlin').format('DD.MM.YYYY')
      case 'date-time':
        return value.tz('Europe/Berlin').format('DD.MM.YYYY HH:mm:ss')
      case 'time':
        return value.tz('Europe/Berlin').format('HH:mm')
      default:
        return defaultValue
    }
  }
  return defaultValue
}
